import * as firebase from 'firebase/app'; //https://stackoverflow.com/questions/42090156/firebase-initializeapp-is-not-a-function\
import {
    getAuth, NextOrObserver, sendPasswordResetEmail, signInAnonymously, signInWithEmailAndPassword,
    signOut, User, UserCredential
} from 'firebase/auth';

import { FirebaseConfig } from '../../configs/FirebaseConfig';
import { ZeosApiConfig } from '../../configs/ZeosApiConfig';
import { Analytics } from '../analytics/Analytics';
import { IZeosUserClaimsContract } from '../zeos-api/contracts/Account';
import { ZeosApiService } from '../zeos-api/ZeosApiService';
import { IZeosUser } from './IZeosUser';

try {
  firebase.initializeApp(FirebaseConfig);
} catch (error) {
  console.log("Auth", error);
}

const firebaseAuth = getAuth(firebase.getApp());

let zeosUserClaims = {} as IZeosUserClaimsContract;
let zeosUser = {} as IZeosUser;
let AuthToken = "";
let newZeosUser = {} as IZeosUser;
let verificationCode = "";
let claimsPromise: Promise<void> | null = null;
let zeosUserPromise: Promise<void> | null = null;

// The pending signup identity used to live only in this module's memory, so any
// full page load during onboarding wiped it -- leaving the verification screen
// with no user to submit the emailed code against and the address permanently
// consumed by an unverified row. Mirror the identifying fields to localStorage
// so a return visit in the same browser can rehydrate them.
const PENDING_SIGNUP_STORAGE_KEY = "pending-signup-identity";

interface IPendingSignup {
  zeosUserId: number;
  emailAddress: string;
  firstName: string;
  lastName: string;
}

const readPendingSignup = (): IPendingSignup | undefined => {
  try {
    const stored = window.localStorage.getItem(PENDING_SIGNUP_STORAGE_KEY);
    if (!stored) {
      return undefined;
    }
    const pending = JSON.parse(stored) as IPendingSignup;
    return pending?.zeosUserId ? pending : undefined;
  } catch (error) {
    console.warn("Failed to read pending signup from localStorage:", error);
    return undefined;
  }
};

const writePendingSignup = (user: IZeosUser) => {
  try {
    if (!user?.zeosUserId) {
      window.localStorage.removeItem(PENDING_SIGNUP_STORAGE_KEY);
      return;
    }
    const pending: IPendingSignup = {
      zeosUserId: user.zeosUserId,
      emailAddress: user.emailAddress,
      firstName: user.firstName,
      lastName: user.lastName,
    };
    window.localStorage.setItem(
      PENDING_SIGNUP_STORAGE_KEY,
      JSON.stringify(pending)
    );
  } catch (error) {
    console.warn("Failed to persist pending signup to localStorage:", error);
  }
};

const clearPendingSignup = () => {
  try {
    window.localStorage.removeItem(PENDING_SIGNUP_STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear pending signup from localStorage:", error);
  }
};

const idToken = async () => {
  return await firebaseAuth.currentUser?.getIdTokenResult();
};

const initClaimsAsync = async () => {
  if (claimsPromise) {
    await claimsPromise;
    return;
  }

  claimsPromise = (async () => {
    const auth = await idToken();
    AuthToken = auth?.token ?? "";
    if (typeof auth?.claims.user_id === "string") {
      ZeosApiService.init(ZeosApiConfig);
      const response = await ZeosApiService.Account.getUserClaimsWebAsync(
        auth?.claims.user_id
      );
      zeosUserClaims = response.data ? response.data : zeosUserClaims;
    }
  })();

  try {
    await claimsPromise;
  } finally {
    claimsPromise = null;
  }
};

const setZeosUser = async () => {
  if (zeosUserPromise) {
    await zeosUserPromise;
    return;
  }

  zeosUserPromise = (async () => {
    //Always resetting claims since they dynamically change during Onboarding and other data-entry processes.
    //Consider if setZeosUser() needs an argument to force reset claims or not
    await initClaimsAsync();
    zeosUser = {
      displayName: `${zeosUserClaims.firstName} ${zeosUserClaims.lastName}`,
      zeosUserId: zeosUserClaims.zeosUserId,
      principalId: zeosUserClaims.principalId,
      authSystemId: zeosUserClaims.authSystemId,
      firstName: zeosUserClaims.firstName,
      lastName: zeosUserClaims.lastName,
      emailAddress: zeosUserClaims.emailAddress,
      phone: zeosUserClaims.phone,
      customerClaims: zeosUserClaims.customerClaims,
      summaryEmailSent: zeosUserClaims.summaryEmailSent,
      isSuperAdmin: zeosUserClaims.isSuperAdmin,
    } as IZeosUser;
    Analytics.setUserContext({
      customerId: zeosUserClaims.customerClaims?.[0]?.customerId,
      customerName: zeosUserClaims.customerClaims?.[0]?.customerName,
      isSuperAdmin: zeosUserClaims.isSuperAdmin,
    });
  })();

  try {
    await zeosUserPromise;
  } finally {
    zeosUserPromise = null;
  }
};

const resetClaimsAsync = async () => {
  await setZeosUser();
};

export const AccountAuth = {
  authTokenAsync: async () => {
    if (AuthToken === "") {
      await setZeosUser();
    }
    return AuthToken;
  },
  /**
   * Drops only the persisted copy of the pending signup. The in-memory value is
   * left alone because the remaining onboarding steps still read it; this is the
   * "clear on successful verification" half, where the deeplink no longer needs
   * to rehydrate anything.
   */
  clearPendingSignupStorage: () => {
    clearPendingSignup();
  },
  directAuth: firebaseAuth,
  /**
   * Returns undefined -- not an empty object -- when there is no pending signup,
   * so `if (!user)` guards in the onboarding screens actually trip.
   */
  getNewZeosUser: (): IZeosUser | undefined => {
    if (newZeosUser?.zeosUserId) {
      return newZeosUser;
    }
    const pending = readPendingSignup();
    if (!pending) {
      return undefined;
    }
    // Rehydrated from storage, so only the identifying fields are real. The rest
    // are re-read from the API once the account is created and signed in.
    newZeosUser = {
      displayName: `${pending.firstName} ${pending.lastName}`,
      zeosUserId: pending.zeosUserId,
      principalId: 0,
      authSystemId: "",
      firstName: pending.firstName,
      lastName: pending.lastName,
      emailAddress: pending.emailAddress,
      phone: "",
      customerClaims: [],
      summaryEmailSent: false,
      isSuperAdmin: false,
    };
    return newZeosUser;
  },
  getVerificationCode: () => {
    return verificationCode;
  },
  idToken: async () => {
    return await idToken();
  },
  initClaimsAsync: async (): Promise<IZeosUserClaimsContract> => {
    await initClaimsAsync();
    return zeosUserClaims;
  },
  observeAuth: (callback: NextOrObserver<any> | ((a: User | null) => any)) => {
    return firebaseAuth.onAuthStateChanged(callback);
  },
  passwordResetAsync: async (email: string) => {
    await sendPasswordResetEmail(firebaseAuth, email);
  },
  resetClaimsAsync: async () => {
    await resetClaimsAsync();
  },
  setNewZeosUser: (user: IZeosUser) => {
    newZeosUser = user;
    writePendingSignup(user);
  },
  setVerificationCode: (code: string) => {
    verificationCode = code;
  },
  signinAnonymousAsync: async (
    successHandler: (user: UserCredential) => Promise<void>,
    errorHandler: (error: any) => Promise<void>
  ) => {
    signInAnonymously(firebaseAuth)
      .then(async (result) => {
        await setZeosUser();
        await successHandler(result);
      })
      .catch(async (error) => {
        await errorHandler(error);
      });
  },
  signInAsync: async (
    email: string,
    password: string,
    successHandler: (user: UserCredential) => Promise<void>,
    errorHandler: (error: any) => Promise<void>
  ) => {
    // setPersistence(firebaseAuth, browserSessionPersistence)
    //   .then(() => {
    signInWithEmailAndPassword(firebaseAuth, email, password)
      .then(async (result) => {
        await setZeosUser();
        await successHandler(result);
      })
      .catch(async (error) => {
        await errorHandler(error);
      });
    // })
    // .catch((error) => {
    //   errorHandler(error);
    // });
  },
  signOutAsync: async (successCallback: () => void) => {
    // Reset in-memory auth caches to avoid leaking previous user's data
    AuthToken = "";
    zeosUser = {} as IZeosUser;
    zeosUserClaims = {} as IZeosUserClaimsContract;
    claimsPromise = null;
    zeosUserPromise = null;
    // Drop the pending signup too, in memory and on disk, so a fresh signup
    // (which signs out first) can never inherit the previous one's identity.
    newZeosUser = {} as IZeosUser;
    verificationCode = "";
    clearPendingSignup();
    try {
      // Must await: callers rely on signOutAsync() not resolving until
      // Firebase has actually cleared the persisted session, otherwise a
      // stale currentUser can still be picked up by the very next auth call.
      await signOut(firebaseAuth);
      successCallback();
    } catch (error) {
      console.log(error);
    }
  },
  zeosUserAsync: async (forceRefresh?: boolean) => {
    const missingUser = (zeosUser.zeosUserId ?? 0) === 0;
    if (missingUser || forceRefresh) {
      await setZeosUser();
    }
    return zeosUser;
  },
};
